import { useEffect, useRef } from 'react'

import { useNavigate } from 'react-router-dom'

import { Layout, SpinnerWheel, Text } from '@loadsmart/miranda-react'

import { type AuthErrorLocationState, toAuthError } from 'auth/domain/AuthError'
import { REDIRECT_TO_STORAGE_KEY } from 'auth/domain/User'
import { getStoredUser } from 'auth/infra/StoredUser'

import useOauth2Flow from 'auth/ui/hooks/useOauth2'
import { AnalyticsEvent } from 'core/analytics/events'
import useAnalytics from 'core/analytics/useAnalytics'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

const LoginThroughBIPCallback = () => {
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const authTrackedRef = useRef(false)
  const storage = new LocalStorageClient()

  function getUserData() {
    const user = getStoredUser(storage)
    return user?.email ? { email: user.email } : null
  }

  function onLoginSuccess() {
    if (!authTrackedRef.current) {
      authTrackedRef.current = true
      const userData = getUserData()
      track(AnalyticsEvent.Authentication, { 
        authFlow: 'BIP', 
        authStage: 'success',
        ...(userData ? { email: userData.email } : {})
      })
    }

    const redirectTo = localStorage.getItem(REDIRECT_TO_STORAGE_KEY)

    if (redirectTo) {
      localStorage.removeItem(REDIRECT_TO_STORAGE_KEY)
    }

    navigate(redirectTo ?? '/demo')
  }

  function onLoginFailure(error: Error) {
    console.error(error)
    const authError = toAuthError(error)
    const userData = getUserData()
    track(AnalyticsEvent.Authentication, {
      authFlow: 'BIP',
      authStage: 'failure',
      // The backend's reason, not axios' "Request failed with status code 401".
      error: authError.code || authError.detail,
      ...(userData ? { email: userData.email } : {})
    })
    navigate('/', { state: { authError } satisfies AuthErrorLocationState })
  }

  const { handleLogInCallback } = useOauth2Flow({ onLoginSuccess, onLoginFailure })

  useEffect(() => {
    handleLogInCallback()
  }, [handleLogInCallback])

  return (
    <Layout.Stack justify="center" align="center" style={{ margin: 'auto', height: '100vh' }}>
      <Text variant="heading-sm-bold">Authentication in progress...</Text>
      <SpinnerWheel size="48" />
    </Layout.Stack>
  )
}

export default LoginThroughBIPCallback
